import { ChevronRightIcon } from '@heroicons/react/20/solid';

import { isOptionalMissingField } from '@/lib/missingFields';
import type { TreatmentRecordRequestChannelType } from '@/services/api/generated/treatmentRecordRequestsService';
import type {
  HipaaPackageSchema,
  PackageComponentSchema,
} from '@/services/api/hipaaPackageService';

import { useRequestPage } from '../contexts/RequestPageContext';

/**
 * Determines the specific CTA text for setting request method based on channel type
 */
const getRequestMethodCTA = (
  channelType: TreatmentRecordRequestChannelType | undefined,
): { text: string } => {
  switch (channelType) {
    case 'fax':
      return { text: 'Set fax number' };
    case 'email':
      return { text: 'Set email address' };
    case 'mail':
      return { text: 'Set mailing address' };
    case 'portal':
      return { text: 'Set portal details' };
    case 'human':
      return { text: 'Set phone number' };
    default:
      return { text: 'Set request method' };
  }
};

/**
 * Analyzes missing fields to determine what actions are needed
 */
const analyzeMissingFields = (packageData: HipaaPackageSchema) => {
  let needsRequestMethod = false;
  let needsSignature = false;

  packageData.components?.forEach((component) => {
    // Skip signed_authorization_merge components - their missing fields are optional
    if (component.component_type === 'signed_authorization_merge') return;
    if (!component.missing_fields) return;

    Object.values(component.missing_fields).forEach((field) => {
      if (isOptionalMissingField(field)) {
        return;
      }
      const contextKey = field.context_key;

      // Check for request method fields
      if (
        contextKey === 'treatmentChannelType' ||
        contextKey === 'treatmentChannelValue'
      ) {
        needsRequestMethod = true;
        return;
      }

      // Check for HIPAA signature date
      if (contextKey === 'clientHipaaSignedDate') {
        needsSignature = true;
      }
    });
  });

  return { needsRequestMethod, needsSignature };
};

interface MissingDataBannerProps {
  onOpenDocumentEditor?: () => void;
}

export const MissingDataBanner = ({
  onOpenDocumentEditor,
}: MissingDataBannerProps = {}) => {
  const {
    packageData,
    request,
    validationStatus,
    hasMissingSignature,
    openModal,
  } = useRequestPage();

  // Safety checks
  if (!packageData || !request) return null;

  const { hasMissingFields } = validationStatus;

  // Get the specific request method CTA based on channel type
  const requestMethodCTA = getRequestMethodCTA(request.channel?.type);

  // Check if package includes a HIPAA authorization template
  const hasHipaaAuthTemplate = packageData?.components?.some(
    (component: PackageComponentSchema) =>
      component.template?.category === 'HIPAA_AUTHORIZATION',
  );

  // Only enforce signature requirement if there's a HIPAA authorization template
  const shouldShowSignatureIssue = hasHipaaAuthTemplate && hasMissingSignature;

  // Don't show if nothing is missing
  if (!hasMissingFields && !shouldShowSignatureIssue) {
    return null;
  }

  // Don't show for manually uploaded packages that are published
  const isManualUploadAndPublished =
    packageData.package_source === 'manually_uploaded' &&
    packageData.status === 'published';

  if (isManualUploadAndPublished) {
    return null;
  }

  // Analyze what's actually missing to show smart CTAs
  const { needsRequestMethod, needsSignature: needsSignatureFromFields } =
    analyzeMissingFields(packageData);

  // Combine signature check from fields and direct signature check
  const needsSignature =
    hasHipaaAuthTemplate &&
    (shouldShowSignatureIssue || needsSignatureFromFields);

  // Build action items
  const actions: Array<{
    key: string;
    label: string;
    onClick: () => void;
  }> = [];

  if (needsRequestMethod) {
    actions.push({
      key: 'requestMethod',
      label: requestMethodCTA.text,
      onClick: () => openModal('requestMethod'),
    });
  }

  if (needsSignature) {
    actions.push({
      key: 'signature',
      label: 'Add signature',
      onClick: () => openModal('signature'),
    });
    actions.push({
      key: 'eSign',
      label: 'Request e-signature',
      onClick: () => openModal('eSign'),
    });
  }

  if (hasMissingFields && onOpenDocumentEditor) {
    actions.push({
      key: 'editDocuments',
      label: `Fix ${validationStatus.missingFieldsCount} field${validationStatus.missingFieldsCount === 1 ? '' : 's'} in editor`,
      onClick: onOpenDocumentEditor,
    });
  }

  // If no smart actions but still have missing fields, show generic message
  if (actions.length === 0 && hasMissingFields) {
    return (
      <div className="flex items-center gap-3 border-t border-zinc-100 bg-white px-6 py-3">
        <span className="text-[13px] text-zinc-500">
          {validationStatus.missingFieldsCount} missing field
          {validationStatus.missingFieldsCount !== 1 ? 's' : ''} to resolve
        </span>
      </div>
    );
  }

  if (actions.length === 0) return null;

  return (
    <div className="flex items-center gap-3 border-t border-zinc-100 bg-white px-6 py-3">
      <span className="text-[13px] text-zinc-400">To submit</span>
      <div className="flex items-center gap-1.5">
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            onClick={action.onClick}
            className="inline-flex items-center gap-1 rounded-xl border border-zinc-200/80
                       bg-zinc-50 px-3 py-1.5 text-[13px] font-medium text-zinc-600
                       hover:border-zinc-300/80 hover:bg-zinc-100 hover:text-zinc-800
                       transition-all"
          >
            {action.label}
            <ChevronRightIcon className="w-3.5 h-3.5 text-zinc-400" />
          </button>
        ))}
      </div>
    </div>
  );
};
